import { FC, useEffect, useMemo, useState } from 'react';
import Breadcrumbs from './components/Breadcrumbs';
import { FileDownload } from '@mui/icons-material';
import HighlightCards from './components/HighlightCards';

import { useFilter } from './contexts/FilterContext';
import SkillTable from './components/SkillTable';
import FilterRow from './components/FilterRow';


type Props = {
  onBack: () => void;
};

const AllSkills: FC<Props> = ({ onBack }) => {
  const { selectedTab, selectedSector } = useFilter();
  const [sectors, setSectors] = useState([]);
  const [occupations, setOccupations] = useState([]);
  const [overallSkills, setOverallSkills] = useState<any>(null);

  useEffect(() => {
    const loadJsonFiles = async () => {
      try {
        const [sectorsData, occupationsData, skillsMeta] = await Promise.all([
          import(`./json/${selectedTab}/sectors.json`),
          import(`./json/${selectedTab}/occupations.json`),
          import(`./json/${selectedTab}/overallSkills.json`)
        ]);
        setSectors(sectorsData.default);
        setOccupations(occupationsData.default);
        setOverallSkills(skillsMeta.default);
      } catch (err) {
        console.error('Error loading data for', selectedTab, err);
        setSectors([]);
        setOccupations([]);
        setOverallSkills(null);
      }
    };

    loadJsonFiles();
  }, [selectedTab]);


  const highlightCards = useMemo(() => {
    if (!overallSkills) return [];

    const sectorSkills = selectedSector && selectedSector !== 'All'
      ? overallSkills.sectors?.find((s: any) => s.code === selectedSector)
      : overallSkills;


    const totalDemand = sectorSkills.relativeDemand || 0;
    const distinctRoles = sectorSkills.distinct_roles || 0;
    const avgGrowth = sectorSkills.growthRate || '+0.0%';

    return [
      {
        icon: (
          <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M17.1275 23.2364H24.2184M17.1275 28.9091H24.2184M17.1275 34.5819H24.2184M29.8911 36H34.1457C36.4954 36 38.4002 34.0952 38.4002 31.7455V12.0956C38.4002 9.94944 36.8024 8.12834 34.6636 7.95082C33.9578 7.89224 33.2496 7.84173 32.5393 7.79939M21.5702 7.79939C21.4479 8.19615 21.382 8.61768 21.382 9.05459C21.382 9.83783 22.017 10.4728 22.8002 10.4728H31.3093C32.0925 10.4728 32.7275 9.83783 32.7275 9.05459C32.7275 8.61768 32.6616 8.19615 32.5393 7.79939M21.5702 7.79939C22.1058 6.06236 23.7238 4.80005 25.6366 4.80005H28.4729C30.3857 4.80005 32.0037 6.06236 32.5393 7.79939M21.5702 7.79939C20.8599 7.84173 20.1517 7.89224 19.4459 7.95082C17.3071 8.12834 15.7093 9.94944 15.7093 12.0956V16.1455M15.7093 16.1455H9.32747C8.15261 16.1455 7.2002 17.0979 7.2002 18.2728V39.5455C7.2002 40.7204 8.15261 41.6728 9.32747 41.6728H27.7638C28.9387 41.6728 29.8911 40.7204 29.8911 39.5455V18.2728C29.8911 17.0979 28.9387 16.1455 27.7638 16.1455H15.7093Z"
              stroke="#043C5B"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ),
        title: totalDemand.toLocaleString(),
        subtitle: 'Total Skills Tracked',
      },
      {
        icon:
          avgGrowth.startsWith('+') ? (
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none">
              <path
                d="M25.8666 14.6666H40.8M40.8 14.6666V29.6M40.8 14.6666L25.8666 29.6L18.4 22.1333L7.19995 33.3333"
                stroke="#043C5B"
                strokeWidth="3.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ) : (
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M25.8669 33.3333H40.8002M40.8002 33.3333V18.4M40.8002 33.3333L25.8669 18.4L18.4002 25.8666L7.2002 14.6666"
                stroke="#043C5B"
                strokeWidth="3.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ),
        title: avgGrowth,
        subtitle: 'Avg. Skill Demand Growth',
      },
      {
        icon: (
          <svg width="49" height="48" viewBox="0 0 49 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M24.333 27C30.9604 27 36.333 21.6274 36.333 15C36.333 8.37258 30.9604 3 24.333 3C17.7056 3 12.333 8.37258 12.333 15C12.333 21.6274 17.7056 27 24.333 27Z"
              stroke="#043C5B"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M13.8002 21.3582L4.83301 37.5L12.9761 37.4607C13.237 37.4593 13.4938 37.5261 13.721 37.6543C13.9482 37.7826 14.138 37.9679 14.2718 38.1919L18.333 45L26.6139 26.7825"
              stroke="#043C5B"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M34.6961 21L43.833 37.5L35.6899 37.4606C35.429 37.4593 35.1722 37.5261 34.945 37.6543C34.7178 37.7825 34.528 37.9678 34.3943 38.1919L30.333 45L24.333 31.8"
              stroke="#043C5B"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M24.333 21C27.6467 21 30.333 18.3137 30.333 15C30.333 11.6863 27.6467 9 24.333 9C21.0193 9 18.333 11.6863 18.333 15C18.333 18.3137 21.0193 21 24.333 21Z"
              stroke="#043C5B"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ),
        title: distinctRoles.toString(),
        subtitle: 'Distinct Roles Tracked',
      },
    ];
  }, []);

  return (
    <div className="labourForecast-jobRole px-4 py-3">
      <Breadcrumbs
        items={[
          { label: 'Forecast Dashboard', onClick: onBack },
          { label: 'View All Jobs' },
        ]}
      />
      <div
        className="container-fluid pb-3 mb-3 px-0 d-flex justify-content-between align-items-center rounded"
        style={{ borderBottom: '1px solid #E9EDF2' }}
      >
        <div>
          <h2 className="mb-1 text-dark fw-semibold">Future of Work Forecast Dashboard</h2>
          <p className="mb-0 text-secondary">
            Understand emerging job roles, key skills, and future workforce needs.
          </p>
        </div>
        <button
          type="button"
          className="btn d-flex align-items-center gap-2 bg-white text-dark border rounded-pill"
          style={{ borderColor: '#D7DAE0' }}
        >
          <FileDownload style={{ width: 20 }} />
          Download Report
        </button>
      </div>
            <div className="container-fluid pb-3 px-0">
                <FilterRow sectors={sectors} occupations={occupations} />
            </div>

      <div className="container-fluid pb-3 px-0">
        <HighlightCards cards={highlightCards} />
      </div>
      <div className="container-fluid pb-3 px-0">
        <SkillTable />
      </div>
    </div>
  );
};

export default AllSkills;
